import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

// WHY: Define interface for HeadTag props
// Manages document head with react-helmet-async
// Sets title, description, favicon, and loads external scripts
// Loads Google Fonts and CookieYes consent management
export interface HeadTagProps {
  title?: string; // Page title
  description?: string; // Meta description
  favIcon?: string; // Favicon URL
  type?: string; // OpenGraph type (not currently used)
  name?: string; // Site name (not currently used)
  cookieScriptId?: string; // CookieYes script ID
  gaTag?: string; // Google Analytics tag (not currently used)
}

function HeadTag({
  title,
  description,
  favIcon,
  type,
  name,
  cookieScriptId,
  gaTag,
}: HeadTagProps) {
  const [cookieEyesLoaded, setCookieEyesLoaded] = useState(false);

  // WHY: Check if CookieYes is already loaded to prevent duplicate scripts
  useEffect(() => {
    const cookieLoaded =
      document.getElementById('cookieyes-banner') ||
      document.getElementById('cookieyes');
    if (cookieLoaded) setCookieEyesLoaded(true);
  }, []);

  return (
    <Helmet>
      {/* Standard metadata tags */}
      {title && <title>{title}</title>}
      {description && (
        <meta name="description" content={title + '-' + description} />
      )}

      {/* WHY: Google Fonts - Multiple font families for theme support */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        href="https://fonts.googleapis.com/css2?family=Aldrich&family=Inter&family=Roboto+Flex:opsz,wght@8..144,300;8..144,400;8..144,500;8..144,600;8..144,700;8..144,800&family=Roboto:ital,wght@0,400;0,500;0,700;1,400&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap"
        rel="stylesheet"
      />

      <link
        href="https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Roboto+Condensed:ital,wght@0,100..900;1,100..900&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap"
        rel="stylesheet"
      />

      <link id="favicon" rel="icon" href={favIcon || 'favicon.png'} />

      {/* End standard metadata tags */}

      {/* WHY: Load CookieYes consent management script if not already loaded */}
      {cookieScriptId && !cookieEyesLoaded && (
        <script
          id="cookieyes"
          type="text/javascript"
          src={`https://cdn-cookieyes.com/client_data/${cookieScriptId}/script.js`}
        ></script>
      )}
    </Helmet>
  );
}

export default HeadTag;
